/**
 * Talking to the identity providers, from the browser's side.
 *
 * Two facts and one navigation. Which providers exist is the server's answer,
 * not a build-time constant; starting one is a POST that comes back with a URL
 * to go to. Neither is GraphQL, for the reason nothing about authentication is
 * (V1.md section 5).
 */
import type { TranslationKey } from '@athena/i18n'

import { AUTH_ENDPOINTS, AUTH_METHODS_ENDPOINT } from '../../lib/apollo.js'
import type { Language } from '../../lib/language.js'

/** The providers V1 offers, in the order the sign-in screen lists them. */
export const SOCIAL_PROVIDERS = ['google', 'microsoft'] as const
export type SocialProviderId = (typeof SOCIAL_PROVIDERS)[number]

function isProvider(value: unknown): value is SocialProviderId {
  return SOCIAL_PROVIDERS.includes(value as SocialProviderId)
}

/**
 * Which providers this deployment has credentials for.
 *
 * Asked of the server rather than read from an environment variable, because
 * the credentials are server-side and a `VITE_` twin would be a second place
 * for the same fact to be wrong — one that is baked into the bundle at build
 * time and cannot follow a deployment that adds a provider.
 *
 * An unreachable API is an empty list, not an error screen: the code-by-email
 * flow below it needs no provider and is the one that will tell the person the
 * API is down, in a sentence, when they press it.
 */
export async function fetchSocialProviders(
  request: typeof globalThis.fetch = globalThis.fetch,
): Promise<SocialProviderId[]> {
  try {
    const response = await request(AUTH_METHODS_ENDPOINT, { credentials: 'include' })
    if (!response.ok) return []
    const body: unknown = await response.json()
    const listed = (body as { socialProviders?: unknown }).socialProviders
    return Array.isArray(listed) ? listed.filter(isProvider) : []
  } catch {
    return []
  }
}

/** The API selects the mode; an unavailable or older server requires OTP. */
export async function fetchEmailSignInMode(): Promise<'email-only' | 'otp'> {
  try {
    const response = await fetch(AUTH_METHODS_ENDPOINT, { credentials: 'include' })
    if (!response.ok) return 'otp'
    const body: unknown = await response.json()
    return (body as { emailSignInMode?: unknown })?.emailSignInMode === 'email-only'
      ? 'email-only'
      : 'otp'
  } catch {
    return 'otp'
  }
}

/**
 * Where the provider sends the person back to.
 *
 * Both URLs carry the page's language, which is the whole of "the locale
 * survives an OAuth redirect" (V1.md section 18). The browser leaves Athena
 * entirely and comes back through the API, so nothing in this tab persists —
 * the only place the language can be kept is in the URL the provider is told
 * to return to.
 *
 * `errorCallbackURL` matters as much as the success one. Without it a refused
 * or cancelled sign-in lands on Better Auth's own default, which knows nothing
 * about `/es` and `/en`, and the person ends up on an unprefixed URL being
 * redirected by their stored preference — in the other language from the one
 * they were reading.
 */
export function providerCallbacks(
  language: Language,
  callbackPath: string,
  origin: string = window.location.origin,
): { callbackURL: string; errorCallbackURL: string } {
  const prefixed = `${origin}/${language}${callbackPath === '/' ? '' : callbackPath}`
  return {
    callbackURL: prefixed,
    // Back to the screen they pressed the button on, with a code the screen
    // words itself. Never the provider's own message: see `providerErrorKey`.
    errorCallbackURL: `${origin}/${language}/sign-in?error=provider_rejected`,
  }
}

export interface StartProviderSignInInput {
  provider: SocialProviderId
  language: Language
  callbackPath: string
  fetch?: typeof globalThis.fetch
  origin?: string
}

/**
 * Starts a provider sign-in, returning the URL to navigate to, or null.
 *
 * Better Auth answers this POST with `{ url, redirect: true }` rather than a
 * 302, because a fetch cannot follow a redirect into a different origin's
 * consent screen. So the browser is moved by the caller, deliberately — see
 * the note on `window.location.assign` in `SocialSignIn`.
 */
export async function startProviderSignIn(input: StartProviderSignInInput): Promise<string | null> {
  const request = input.fetch ?? globalThis.fetch

  try {
    const response = await request(AUTH_ENDPOINTS.signInWithProvider, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        provider: input.provider,
        ...providerCallbacks(input.language, input.callbackPath, input.origin),
      }),
    })
    if (!response.ok) return null

    const body: unknown = await response.json()
    const url = (body as { url?: unknown }).url
    return typeof url === 'string' && url.length > 0 ? url : null
  } catch {
    return null
  }
}

/**
 * What a provider round trip came back saying, as a dictionary key.
 *
 * The API never forwards a provider's own error text — it can name an internal
 * endpoint, quote a request, or simply be English in a Spanish page — so what
 * arrives is a code in the query string and this is the only place that maps
 * one to a sentence. An unrecognised code is the generic refusal rather than
 * the code itself: printing an unknown string at a person is how a stack trace
 * ends up on a sign-in screen.
 */
export function providerErrorKey(code: string | null | undefined): TranslationKey | null {
  switch (code) {
    case null:
    case undefined:
    case '':
      return null
    case 'access_denied':
    case 'user_cancelled':
      return 'auth.providerCancelled'
    case 'email_not_provided':
    case 'no_email':
      return 'auth.providerNoEmail'
    default:
      return 'auth.providerRejected'
  }
}
